import React from "react";
import ButtonSaveModelPreset from "domains/assets/components/AssetZoom/Header/ButtonSaveModelPreset";
import {
  useAssetCanvasLink,
  useAssetConvertTo3DLink,
  useAssetConvertToVideoLink,
  useAssetPromptEditLink,
  useAssetSearchSimilar,
} from "domains/assets/hooks/useAssetLink";
import {
  getIsAssetInferenceTexture,
  getIsAssetSkybox,
  getIsAssetUpscaledTexture,
} from "domains/assets/utils/getIsType";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Icon from "domains/ui/components/Icon";
import MenuButtonIcon from "domains/ui/components/Menu/MenuButtonIcon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";

import { Menu, MenuDivider, MenuList } from "@chakra-ui/react";

export interface ButtonMenuProps {
  asset?: GetAssetsByAssetIdApiResponse["asset"];
  onAction?: () => void;
}

export default function ButtonMenu({ asset, onAction }: ButtonMenuProps) {
  const { isReader } = useTeamContext();
  const router = useRouter();
  const editCapabilities = asset?.editCapabilities;
  const isDisabled = !asset;
  const isSkybox = getIsAssetSkybox(asset);
  const isTexture =
    getIsAssetInferenceTexture(asset) ||
    getIsAssetUpscaledTexture(asset) ||
    asset?.metadata?.type.startsWith("texture");

  const linkEditInCanvas = useAssetCanvasLink(asset);
  const linkPromptEdit = useAssetPromptEditLink(asset);
  const linkConvertTo3D = useAssetConvertTo3DLink(asset);
  const linkConvertToVideo = useAssetConvertToVideoLink(asset);
  const linkSearchSimilar = useAssetSearchSimilar(asset);

  const canRemoveBackground =
    editCapabilities?.includes("REMOVE_BACKGROUND") &&
    !isSkybox &&
    !isTexture &&
    !isReader;
  const canPixelate =
    editCapabilities?.includes("PIXELATE") &&
    !isSkybox &&
    !isTexture &&
    !isReader;
  const canReframe =
    editCapabilities?.includes("REFRAME") &&
    !isSkybox &&
    !isTexture &&
    !isReader;
  const canVectorize =
    editCapabilities?.includes("VECTORIZATION") &&
    !isSkybox &&
    !isTexture &&
    !isReader;
  const canPromptEdit = linkPromptEdit && !isReader;
  const canEditInCanvas = linkEditInCanvas && !isReader;
  const canSavePreset = !!asset?.metadata.inferenceId && !isReader;
  const canConvertTo3D = linkConvertTo3D && !isReader;
  const canConvertToVideo = linkConvertToVideo && !isReader;

  const hasUseSection = canSavePreset || linkSearchSimilar;
  const hasEditSection =
    canEditInCanvas ||
    canReframe ||
    canRemoveBackground ||
    canPixelate ||
    canVectorize ||
    canPromptEdit;
  const hasConvertSection = canConvertTo3D || canConvertToVideo;

  const hasActions = hasUseSection || hasEditSection || hasConvertSection;

  if (!hasActions) {
    return null;
  }

  return (
    <Menu>
      <MenuButtonIcon
        aria-label="More"
        data-testid="asset-zoom-three-dots-menu-button"
      >
        <Icon id="Ui/Dots" />
      </MenuButtonIcon>
      <MenuList maxH="unset">
        {hasConvertSection && (
          <>
            {canConvertTo3D && (
              <MenuItem
                internalLink={linkConvertTo3D}
                text="Convert to 3D"
                isDisabled={isDisabled}
                onClick={onAction}
              />
            )}
            {canConvertToVideo && (
              <MenuItem
                internalLink={linkConvertToVideo}
                text="Convert to Video"
                isDisabled={isDisabled}
                onClick={onAction}
              />
            )}
            {(hasEditSection || hasUseSection) && <MenuDivider mx={2} />}
          </>
        )}

        {hasEditSection && (
          <>
            {canEditInCanvas && (
              <MenuItem
                data-testid="three-dots-menu-edit-details"
                internalLink={linkEditInCanvas}
                target="_blank"
                text="Retouch"
              />
            )}
            {canReframe && (
              <MenuItem
                internalLink={{
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    edit: "expand",
                  },
                }}
                text="Expand"
                isDisabled={isDisabled}
                onClick={onAction}
              />
            )}
            {canRemoveBackground && (
              <MenuItem
                internalLink={{
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    edit: "removeBg",
                  },
                }}
                text="Remove background"
                isDisabled={isDisabled}
                onClick={onAction}
              />
            )}
            {canPixelate && (
              <MenuItem
                internalLink={{
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    edit: "pixelate",
                  },
                }}
                text="Pixelate"
                isDisabled={isDisabled}
                onClick={onAction}
              />
            )}
            {canVectorize && (
              <MenuItem
                internalLink={{
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    edit: "vectorize",
                  },
                }}
                text="Vectorize"
                isDisabled={isDisabled}
                onClick={onAction}
              />
            )}
            {canPromptEdit && (
              <MenuItem
                internalLink={linkPromptEdit}
                text="Edit with Prompts"
                isDisabled={isDisabled}
                onClick={onAction}
              />
            )}
            {hasUseSection && <MenuDivider mx={2} />}
          </>
        )}

        {hasUseSection && (
          <>
            {!!linkSearchSimilar && (
              <MenuItem
                text="Find similar content"
                internalLink={linkSearchSimilar}
                isDisabled={isDisabled}
              />
            )}
            {canSavePreset && (
              <ButtonSaveModelPreset isDisabled={isDisabled} asset={asset} />
            )}
          </>
        )}
      </MenuList>
    </Menu>
  );
}
